import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, Loader2 } from "lucide-react";

const FUNCTIONAL_LIMIT_OPTIONS = ["Ambulation", "Bed Mobility", "Transfer", "Eating", "Toileting", "Bathing", "Dressing", "Grooming", "Vision", "Hearing", "Endurance", "Speech", "Legally Blind", "Dyspnea With"];
const MENTAL_STATUS_OPTIONS = ["Oriented", "Comatose", "Forgetful", "Depressed", "Disoriented", "Lethargic", "Agitated", "Other"];
const PROGNOSIS_OPTIONS = ["poor", "guarded", "fair", "good", "excellent"];

function CheckGrid({ options, value, onChange, testid }) {
  const toggle = (opt) => onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1" data-testid={testid}>
      {options.map((opt) => (
        <label key={opt} className="flex items-center gap-2 text-xs text-slate-700 rounded-md border border-slate-200 px-2 py-1.5 hover:bg-slate-50 cursor-pointer">
          <Checkbox checked={value.includes(opt)} onCheckedChange={() => toggle(opt)} data-testid={`${testid}-${opt.replace(/\s+/g, "-").toLowerCase()}`} />
          {opt}
        </label>
      ))}
    </div>
  );
}

export default function ClinicalSummaryDialog({ open, onClose, suggestion, typeLabel, onConfirm, busy }) {
  const [s, setS] = useState({});
  useEffect(() => {
    if (open) setS({ mental_status: [], functional_limitations: [], prognosis: "good", ...(suggestion || {}) });
  }, [open, suggestion]);
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="clinical-summary-dialog">
        <DialogHeader>
          <DialogTitle>Clinical summary for the CMS-485</DialogTitle>
          <DialogDescription>
            Confirm the key findings from this {typeLabel} assessment. They'll pre-fill the client's next CMS-485 so nothing has to be re-typed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Principal diagnosis</Label>
              <Input value={s.principal_diagnosis || ""} onChange={(e) => set("principal_diagnosis", e.target.value)} data-testid="summary-principal-dx" />
            </div>
            <div>
              <Label className="text-xs">Other diagnoses</Label>
              <Input value={s.other_diagnoses || ""} onChange={(e) => set("other_diagnoses", e.target.value)} data-testid="summary-other-dx" />
            </div>
            <div>
              <Label className="text-xs">Allergies</Label>
              <Input value={s.allergies || ""} onChange={(e) => set("allergies", e.target.value)} data-testid="summary-allergies" />
            </div>
            <div>
              <Label className="text-xs">Prognosis</Label>
              <Select value={s.prognosis || "good"} onValueChange={(v) => set("prognosis", v)}>
                <SelectTrigger data-testid="summary-prognosis"><SelectValue /></SelectTrigger>
                <SelectContent>{PROGNOSIS_OPTIONS.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Mental status</Label>
            <CheckGrid options={MENTAL_STATUS_OPTIONS} value={s.mental_status || []} onChange={(v) => set("mental_status", v)} testid="summary-mental" />
          </div>
          <div>
            <Label className="text-xs">Functional limitations</Label>
            <CheckGrid options={FUNCTIONAL_LIMIT_OPTIONS} value={s.functional_limitations || []} onChange={(v) => set("functional_limitations", v)} testid="summary-functional" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">DME / supplies</Label>
              <Textarea rows={2} value={s.dme_supplies || ""} onChange={(e) => set("dme_supplies", e.target.value)} data-testid="summary-dme" />
            </div>
            <div>
              <Label className="text-xs">Safety measures</Label>
              <Textarea rows={2} value={s.safety_measures || ""} onChange={(e) => set("safety_measures", e.target.value)} data-testid="summary-safety" />
            </div>
            <div>
              <Label className="text-xs">Nutritional requirements</Label>
              <Textarea rows={2} value={s.nutritional_requirements || ""} onChange={(e) => set("nutritional_requirements", e.target.value)} data-testid="summary-nutrition" />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onConfirm(null)} disabled={busy} data-testid="summary-skip-btn">Skip — complete without summary</Button>
          <Button onClick={() => onConfirm(s)} disabled={busy} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="summary-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Complete assessment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
